// src/components/ODRecord.jsx
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Alert
} from '@mui/material';

// Icons
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PlaceIcon from '@mui/icons-material/Place';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HistoryIcon from '@mui/icons-material/History';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`od-tabpanel-${index}`}
    aria-labelledby={`od-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const ODRecord = () => {
  const [formData, setFormData] = useState({
    empCode: '',
    name: '',
    startDate: '',
    endDate: '',
    location: '',
    reason: '',
  });

  const [isApprover, setIsApprover] = useState(false);
  const [ODRecords, setODRecords] = useState([]);
  const [ODHistory, setODHistory] = useState([]);
  const [tab, setTab] = useState(0);
  const [managerName, setManagerName] = useState('');
  const [loadingManager, setLoadingManager] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ show: false, type: 'info', message: '' });

  const token = localStorage.getItem('token');

  const checkApproverImmediately = (code) => {
    axios.get('https://gannondunkerley.in/employees/', {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const employees = res.data.EmployeeList || [];
      const hasSubordinates = employees.some(emp => emp.reportingManager === code);
      setIsApprover(hasSubordinates);

      const currentUser = employees.find(e => e.empCode === code);
      const manager = employees.find(e => e.empCode === currentUser?.reportingManager);
      setManagerName(manager ? `${manager.name} (#${manager.empCode})` : 'None Assigned');
      setLoadingManager(false);
    }).catch(err => {
      console.error("Failed to fetch employee details", err);
      setLoadingManager(false);
    });
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get('https://gannondunkerley.in/ODRecord/history', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setODHistory(res.data || []);
    } catch (err) {
      console.error("Failed to fetch OD history", err);
    }
  };

  const fetchPendingApprovals = () => {
    axios.get('https://gannondunkerley.in/ODRecord/pending', {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setODRecords(res.data || []);
    }).catch(err => console.error("Failed to fetch pending OD requests", err));
  };

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const code = payload.empCode;
        setFormData(prev => ({
          ...prev,
          empCode: code,
          name: payload.username || payload.name || '',
        }));
        checkApproverImmediately(code);
        fetchHistory();
      } catch (e) {
        console.error("Error decoding token", e);
        setLoadingManager(false);
      }
    }
  }, []);

  useEffect(() => {
    if (isApprover) {
      fetchPendingApprovals();
    }
  }, [isApprover]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculatedDays = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return 0;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (end < start) return 0;
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [formData.startDate, formData.endDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setAlertInfo({ show: true, type: 'error', message: 'End Date cannot be earlier than Start Date.' });
      return;
    }

    setSubmitting(true);
    setAlertInfo({ show: false, type: 'info', message: '' });

    try {
      await axios.post('https://gannondunkerley.in/ODRecord', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlertInfo({
        show: true,
        type: 'success',
        message: 'On Duty application submitted successfully! Reporting manager notified via email.'
      });

      setFormData(prev => ({
        ...prev,
        startDate: '',
        endDate: '',
        location: '',
        reason: '',
      }));
      fetchHistory();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'Failed to submit On Duty application.';
      setAlertInfo({ show: true, type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (id, status) => {
    try {
      await axios.put(`https://gannondunkerley.in/ODRecord/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setODRecords(prev => prev.filter(lr => lr._id !== id));
      alert(`On Duty request marked as ${status.toUpperCase()}. Applicant notified.`);
    } catch (err) {
      console.error("Approval error:", err);
      alert("Failed to update status.");
    }
  };

  const getStatusChip = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'approved') {
      return <Chip label="Approved" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 700 }} />;
    }
    if (s === 'rejected') {
      return <Chip label="Rejected" size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700 }} />;
    }
    return <Chip label="Pending" size="small" sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 700 }} />;
  };

  return (
    <Box className="content" sx={{ p: { xs: 2, md: 3.5 }, bgcolor: '#f4f6fa', minHeight: '100vh' }}>
      
      {/* HEADER BANNER */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
          On Duty (OD) Management Portal
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
          Log official site travel, client field visits, and track approval status
        </Typography>
      </Box>

      {/* METRIC CARDS */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                Reporting Authority
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                {loadingManager ? 'Fetching...' : managerName}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#ede9fe', color: '#7c3aed', width: 48, height: 48, borderRadius: 2.5 }}>
              <SupervisorAccountIcon />
            </Avatar>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                My Completed OD Logs
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                {ODHistory.filter(h => h.approvalStatus === 'approved').length} <span style={{ fontSize: '1rem', color: '#64748b' }}>Records</span>
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0284c7', width: 48, height: 48, borderRadius: 2.5 }}>
              <BusinessCenterIcon />
            </Avatar>
          </Paper>
        </Grid>

        {isApprover && (
          <Grid item xs={12} sm={12} md={4}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                  Pending OD Approvals
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#ea580c', mt: 0.5 }}>
                  {ODRecords.length} <span style={{ fontSize: '1rem', color: '#64748b' }}>Requests</span>
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#ffedd5', color: '#ea580c', width: 48, height: 48, borderRadius: 2.5 }}>
                <PendingActionsIcon />
              </Avatar>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* NAVIGATION TABS */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(e, val) => setTab(val)}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
            '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '0.92rem', py: 2 }
          }}
        >
          <Tab icon={<BusinessCenterIcon sx={{ fontSize: 18, mr: 0.5 }} />} iconPosition="start" label="Apply On Duty" />
          <Tab icon={<HistoryIcon sx={{ fontSize: 18, mr: 0.5 }} />} iconPosition="start" label="OD History" />
          {isApprover && (
            <Tab icon={<PendingActionsIcon sx={{ fontSize: 18, mr: 0.5 }} />} iconPosition="start" label={`Pending Approvals (${ODRecords.length})`} />
          )}
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* TAB 1: APPLY ON DUTY */}
          <TabPanel value={tab} index={0}>
            {alertInfo.show && (
              <Alert severity={alertInfo.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setAlertInfo({ ...alertInfo, show: false })}>
                {alertInfo.message}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 720 }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Employee Code"
                    value={formData.empCode}
                    disabled
                    size="small"
                    InputProps={{ sx: { bgcolor: '#f8fafc', borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Applicant Name"
                    value={formData.name}
                    disabled
                    size="small"
                    InputProps={{ sx: { bgcolor: '#f8fafc', borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    name="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
                </Grid>

                {calculatedDays > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 600 }}>
                        Estimated Duration: <strong>{calculatedDays} Day(s)</strong> On Duty
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Destination / Site Location"
                    placeholder="e.g. DVC Durgapur Plant, IOCL Haldia, Client HQ"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    size="small"
                    InputProps={{
                      startAdornment: <PlaceIcon sx={{ color: '#0284c7', fontSize: 18, mr: 1 }} />,
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Purpose / Official Work Description"
                    placeholder="State detailed technical task or client meeting details..."
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    required
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.2,
                      fontWeight: 700,
                      textTransform: 'none',
                      bgcolor: '#0284c7',
                      '&:hover': { bgcolor: '#0369a1' }
                    }}
                  >
                    {submitting ? 'Submitting Application...' : 'Submit & Notify Manager'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {/* TAB 2: OD HISTORY */}
          <TabPanel value={tab} index={1}>
            {ODHistory.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center', color: '#94a3b8' }}>
                <HistoryIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  No On Duty records found in your personal profile.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 800, color: '#334155', py: 1.5 } }}>
                      <TableCell>From Date</TableCell>
                      <TableCell>To Date</TableCell>
                      <TableCell>Assigned Location</TableCell>
                      <TableCell>Purpose</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ODHistory.map((record) => (
                      <TableRow key={record._id} hover sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {new Date(record.startDate).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {new Date(record.endDate).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#0284c7' }}>
                          {record.location}
                        </TableCell>
                        <TableCell sx={{ color: '#64748b' }}>{record.reason}</TableCell>
                        <TableCell>{getStatusChip(record.approvalStatus)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* TAB 3: PENDING APPROVALS (APPROVER ONLY) */}
          {isApprover && (
            <TabPanel value={tab} index={2}>
              {ODRecords.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center', color: '#94a3b8' }}>
                  <CheckCircleIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#475569' }}>
                    All requests processed!
                  </Typography>
                  <Typography variant="body2">
                    No pending On Duty requests require your review at this time.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 800, color: '#334155', py: 1.5 } }}>
                        <TableCell>Employee</TableCell>
                        <TableCell>Emp Code</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell align="center">Decision</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ODRecords.map((req) => (
                        <TableRow key={req._id} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{req.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>#{req.empCode}</TableCell>
                          <TableCell>{new Date(req.startDate).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell>{new Date(req.endDate).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#0284c7' }}>{req.location}</TableCell>
                          <TableCell sx={{ color: '#475569', maxWidth: 220 }}>{req.reason}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => handleApproval(req._id, "approved")}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon />}
                                onClick={() => handleApproval(req._id, "rejected")}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
                              >
                                Reject
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ODRecord;